import { z } from 'zod';
import { BuildingIdSchema } from '../ids';
import { ItemAmountsSchema, ItemIdSchema, QuantitySchema } from './items';
import { SiteRefSchema } from './lot';
import { StructureDefIdSchema } from './town';

/** The id of a unique object (ITEM-INSTANCE). */
export const ObjectIdSchema = z.uuid();
export type ObjectId = z.infer<typeof ObjectIdSchema>;

/** A unique object (ITEM-INSTANCE) as its holder sees it: which one, and what it is. */
export const ObjectViewSchema = z.object({
  id: ObjectIdSchema,
  item: ItemIdSchema,
  /** `[current, max]` for what wears (ITEM-WEAR, RES-GATHER RG-9); its owner's view only. */
  durability: z.tuple([z.number().nonnegative(), z.number().positive()]).optional(),
});
export type ObjectView = z.infer<typeof ObjectViewSchema>;

/**
 * A container of items as its owner sees it: amounts of goods and unique objects. `load` and
 * `capacity` are in load units: each item weighs a configured number of units (iron is heavier
 * than wood), objects included.
 */
export const ContainerViewSchema = z.object({
  items: ItemAmountsSchema,
  objects: z.array(ObjectViewSchema),
  load: QuantitySchema,
  capacity: QuantitySchema,
  /** The most it takes: a backpack carries past its capacity, slower (B055); a storage does not. */
  limit: QuantitySchema,
  /**
   * The perishable goods' batches still good (ITEM-PERISH): per good, `[quantity, expiresAt]`
   * (server game time), soonest first. `items` already leaves out what went bad.
   */
  perishing: z
    .record(ItemIdSchema, z.array(z.tuple([QuantitySchema, z.number().int().nonnegative()])))
    .optional(),
});
export type ContainerView = z.infer<typeof ContainerViewSchema>;

/**
 * A storage that only keeps goods (multiple storages): a cabin's, a shed's, on a town's plot or a
 * lot. It pays nothing; goods go in and out by hand, standing within its reach.
 */
export const StorageViewSchema = ContainerViewSchema.extend({
  buildingId: BuildingIdSchema,
  kind: StructureDefIdSchema,
  site: SiteRefSchema,
});
export type StorageView = z.infer<typeof StorageViewSchema>;

/**
 * A character's room at an inn (HOME technique 9, plan C2): its chest, which inn, until when it is
 * paid (game time; past it nothing comes out) and whether it is safe (an inn in a sanctuary, RV-14).
 */
export const RoomViewSchema = ContainerViewSchema.extend({
  inn: z.string(),
  /** Its number at its inn (AR-2): what a thief names it by. */
  number: z.number().int().positive(),
  /** Its lock was forced (AR-3): open to anyone by its inn's door until its owner relocks it. */
  forced: z.boolean(),
  paidUntil: z.number().int().nonnegative(),
  safe: z.boolean(),
  /** Its size (HOME TI-3): an index of the lodging's sizes. */
  size: z.number().int().nonnegative().optional(),
});
export type RoomView = z.infer<typeof RoomViewSchema>;

/**
 * The player's backpack, the main storage of their own fixed lot (the one that pays and receives
 * in its economy) and the other storages they hold.
 */
export const InventorySchema = z.object({
  backpack: ContainerViewSchema,
  /** None without a fixed lot (the launch world, C9.2): a character arrives homeless (TI-12). */
  storage: ContainerViewSchema.extend({ buildingId: BuildingIdSchema }).nullable(),
  /** The other storages the player holds, in id order (the main one is `storage`). */
  storages: z.array(StorageViewSchema),
  /** The room the player rents at an inn, if any (HOME, plan C2). */
  room: RoomViewSchema.nullable(),
  /** The player's currency balance (PrimeraVersion.md §2: one game currency). */
  coins: z.number().int().nonnegative(),
  /** What the character wears, one object per slot (EQUIP-SLOT). */
  equipment: z.array(ObjectViewSchema.extend({ slot: z.string() })),
  /**
   * Load units per unit of every item the game defines, in definition order (server rules;
   * clients never assume defaults, and list items in this order). Ammunition weighs 0
   * (ITEM-AMMO): its quiver caps it by count.
   */
  weights: z.record(ItemIdSchema, z.number().multipleOf(0.5).nonnegative()),
});
export type Inventory = z.infer<typeof InventorySchema>;

/** Direction of a transfer between the backpack and the town storage. */
export const TransferDirectionSchema = z.enum(['deposit', 'withdraw']);
export type TransferDirection = z.infer<typeof TransferDirectionSchema>;
